import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  CalendarDays,
  FileText,
  LayoutDashboard,
  LogOut,
  ReceiptText,
  Route,
  Settings,
  SlidersHorizontal,
  Users,
} from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const navItems = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'New Quote', href: '/quote', icon: FileText },
  { label: 'Pending', href: '/quotes/pending', icon: BarChart3 },
  { label: 'Clients', href: '/clients', icon: Users },
  { label: 'Schedule', href: '/schedule', icon: CalendarDays },
  { label: 'Routes', href: '/routes', icon: Route },
  { label: 'Invoicing', href: '/invoicing', icon: ReceiptText, comingSoon: true },
  { label: 'Settings', href: '/settings', icon: SlidersHorizontal },
];

function isActivePath(pathname, href) {
  if (href === '/dashboard') return pathname === href;
  if (href === '/settings') return pathname.startsWith('/settings');
  if (href === '/quotes/pending') return pathname === href;
  return pathname === href || pathname.startsWith(`${href}/`);
}

function BrandMark({ size = 'h-9 w-9' }) {
  return (
    <div className={`flex ${size} items-center justify-center rounded-md bg-emerald-50`}>
      <img src="/brand/gq-logo.png" alt="GreenQuote" className="h-7 w-7 object-contain" />
    </div>
  );
}

export default function AppShell({
  children,
  title,
  description,
  accountName,
  userLabel,
  actions,
  maxWidth = 'max-w-7xl',
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-slate-200 bg-white lg:flex lg:flex-col">
        <div className="flex h-16 items-center gap-3 border-b border-slate-200 px-6">
          <BrandMark />
          <div>
            <p className="text-sm font-semibold text-slate-950">GreenQuote</p>
            <p className="text-xs text-slate-500">Lawn care quoting</p>
          </div>
        </div>

        <nav className="flex-1 space-y-1 px-3 py-4">
          {navItems.map((item) => {
            const active = isActivePath(location.pathname, item.href);
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  active
                    ? 'bg-emerald-50 text-emerald-800'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
                )}
              >
                <Icon className="h-4 w-4" />
                <span className="flex-1">{item.label}</span>
                {item.comingSoon && (
                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                    Coming soon
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-slate-200 p-4">
          <p className="truncate text-sm font-medium text-slate-800">{accountName || userLabel || 'Account'}</p>
          {userLabel && accountName && <p className="truncate text-xs text-slate-500">{userLabel}</p>}
          <Button
            type="button"
            variant="ghost"
            className="mt-3 w-full justify-start text-slate-600 hover:text-slate-950"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4" />
            Log out
          </Button>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
          <div className="mx-auto flex min-h-16 max-w-7xl flex-col gap-3 px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="mb-2 flex items-center gap-2 lg:hidden">
                  <BrandMark size="h-8 w-8" />
                  <span className="text-sm font-semibold text-slate-950">GreenQuote</span>
                </div>
                <h1 className="text-2xl font-semibold tracking-normal text-slate-950">{title}</h1>
                {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
              </div>
              {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
            </div>

            <nav className="flex gap-2 overflow-x-auto pb-1 lg:hidden">
              {navItems.map((item) => {
                const active = isActivePath(location.pathname, item.href);
                const Icon = item.icon;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn(
                      'inline-flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-sm font-medium',
                      active
                        ? 'bg-emerald-50 text-emerald-800'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                    {item.comingSoon && (
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                        Soon
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
          </div>
        </header>

        <main className={cn('mx-auto px-4 py-6 sm:px-6 lg:px-8', maxWidth)}>{children}</main>
      </div>
    </div>
  );
}

export function PageLoading({ label = 'Loading...' }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-emerald-700" />
        <p className="mt-4 text-sm text-slate-500">{label}</p>
      </div>
    </div>
  );
}
